import { useLocation } from 'react-router';

import { Translate } from '@change-corgi/core/react/i18n';
import { useNavigate } from '@change-corgi/core/react/router';
import { useTracking } from '@change-corgi/core/react/tracking';
import { Button } from '@change-corgi/design-system-legacy/components/actions';
import { InlineMessage } from '@change-corgi/design-system-legacy/components/alerts';
import { Box, Flex } from '@change-corgi/design-system-legacy/layout';
import { createModalHook } from '@change-corgi/design-system-legacy/modals';
import { Text } from '@change-corgi/design-system-legacy/typography';

import type { PetitionSettingsPageData } from 'src/app/pages/petitionEditV2/pageContext';
import { isError, isLoading } from 'src/app/shared/utils/async';

import { CLOSE_PETITION_ERROR_MAP } from './constants';
import { useClosePetition } from './useClosePetition';

type ClosePetitionProps = {
	petitionId: string;
	promotionStats: PetitionSettingsPageData['petition']['promotionStats'];
	trackingData: { signatureCount: number };
};
type ClosePetitionResult = { status: 'ok' | 'cancelled' };
export const useClosePetitionModal = createModalHook<ClosePetitionProps, ClosePetitionResult>({
	name: 'ClosePetitionModal',
	title: () => (
		<Translate value="corgi.petition.starter_dashboard.petition_status.declare_victory.close_confirmation_modal.title" />
	),
	body: function ClosePetitionModalBody({ closeModal, options: { petitionId, promotionStats, trackingData } }) {
		const navigate = useNavigate();
		const { pathname, search } = useLocation();
		const track = useTracking();
		const {
			mutate: closePetition,
			reset,
			...closePetitionState
		} = useClosePetition(petitionId, {
			onSuccess: () => {
				void track('close_petition_success', { signature_count: trackingData.signatureCount });
				closeModal({ status: 'ok' });
			},
			onError: (error) => {
				void track('close_petition_error', { signature_count: trackingData.signatureCount });
				if (error === CLOSE_PETITION_ERROR_MAP.UNAUTHENTICATED_ERROR) {
					const redirectUrl = encodeURIComponent(pathname + search);
					navigate(`/login_or_join/redirected?redirectTo=${redirectUrl}`);
				}
			},
		});

		const handleClosePetition = async () => {
			void track('close_petition_modal_engaged', { signature_count: trackingData.signatureCount });
			await closePetition();
		};

		const hasUnknownError =
			isError(closePetitionState) && closePetitionState.error !== CLOSE_PETITION_ERROR_MAP.UNAUTHENTICATED_ERROR;

		const hasUnfulfilledPromotions = promotionStats && promotionStats.impressionsRemaining > 0;

		return (
			<Box>
				<Text as="p">
					<Translate value="corgi.petition.starter_dashboard.petition_status.declare_victory.close_confirmation_modal.paragraph_1" />
				</Text>
				{hasUnfulfilledPromotions && (
					<Text as="p" mt={16}>
						<Translate value="corgi.petition.starter_dashboard.petition_status.declare_victory.close_confirmation_modal.paragraph_2_impressions_remaining" />
					</Text>
				)}
				{hasUnknownError && (
					<InlineMessage variant="error" mt={8} data-testid="close-error">
						<Translate value="corgi.petition.starter_dashboard.petition_status.declare_victory.close_confirmation_modal.close_error_message" />
					</InlineMessage>
				)}
				<Flex mt={24} sx={{ gap: 16 }}>
					<Button
						data-testid="close-petition-cancel"
						data-qa="close-petition-cancel"
						variant="secondary"
						onClick={() => closeModal({ status: 'cancelled' })}
					>
						<Translate value="corgi.petition.starter_dashboard.petition_status.declare_victory.close_confirmation_modal.cancel_button" />
					</Button>
					<Button
						data-testid="close-petition-confirm"
						data-qa="close-petition-confirm"
						onClick={handleClosePetition}
						loading={isLoading(closePetitionState)}
						sx={{
							backgroundColor: '#fbdb00',
							borderColor: '#fbdb00',
							'& > span': { color: '#000' },
							'&:hover': { filter: 'brightness(1)' },
						}}
					>
						<Translate value="corgi.petition.starter_dashboard.petition_status.declare_victory.close_confirmation_modal.close_petition_button" />
					</Button>
				</Flex>
			</Box>
		);
	},
});
